import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight, CalendarDots } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { formatDate } from "../lib/utils-extra";

const TYPE_COLOR = {
  upcoming: { dot: "bg-[#0EA5E9]", text: "text-[#0EA5E9]", label: "Upcoming" },
  completed: { dot: "bg-[#16A34A]", text: "text-[#16A34A]", label: "Completed" },
  overdue: { dot: "bg-[#DC2626]", text: "text-[#DC2626]", label: "Overdue" },
  draft: { dot: "bg-[#D97706]", text: "text-[#D97706]", label: "Draft" },
  coaching: { dot: "bg-[#9333EA]", text: "text-[#9333EA]", label: "Coaching" },
};

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => { api.get("/calendar").then(({ data }) => setEvents(data)); }, []);

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const firstDay = cursor.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      if (!e.date) return;
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      m[key] = m[key] || [];
      m[key].push(e);
    });
    return m;
  }, [events]);

  const move = (delta) => {
    const c = new Date(cursor); c.setMonth(c.getMonth() + delta); setCursor(c);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Upcoming list (sorted)
  const upcoming = [...events]
    .filter((e) => new Date(e.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 8);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Schedule</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Calendar</h1>
          <p className="text-[#475569] text-sm mt-1">Every review, follow-up, and coaching session.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="cal-prev"><CaretLeft size={16} /></Button>
          <div className="px-3 py-1.5 rounded-lg bg-[#F1F5F9] font-medium text-sm w-44 text-center">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="cal-next"><CaretRight size={16} /></Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TYPE_COLOR).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${v.dot}`} />
            <span className="text-[#475569]">{v.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="surface-card border-[#E2E8F0] lg:col-span-2">
          <CardContent className="p-3 sm:p-5">
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-[#475569] mb-2">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (d == null) return <div key={i} className="h-20 sm:h-24" />;
                const date = new Date(cursor.getFullYear(), cursor.getMonth(), d);
                const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                const evts = byDay[key] || [];
                const isToday = date.getTime() === today.getTime();
                return (
                  <div key={i} className={`h-20 sm:h-24 p-1.5 rounded-lg border ${isToday ? "border-[#0EA5E9] bg-[#0EA5E9]/5" : "border-transparent"} hover:bg-[#F1F5F9] overflow-hidden`}>
                    <div className={`text-xs font-medium mb-1 ${isToday ? "text-[#0EA5E9]" : "text-[#475569]"}`}>{d}</div>
                    <div className="space-y-1">
                      {evts.slice(0, 2).map((e, idx) => {
                        const col = TYPE_COLOR[e.type] || TYPE_COLOR.upcoming;
                        return (
                          <div key={idx} className="flex items-center gap-1 text-[10px] truncate">
                            <div className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
                            <span className="truncate">{e.title.split(" — ")[1] || e.title}</span>
                          </div>
                        );
                      })}
                      {evts.length > 2 && <div className="text-[10px] text-[#475569]">+{evts.length - 2}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-5">
            <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
              <CalendarDots size={18} weight="duotone" color="#0EA5E9" /> Upcoming
            </div>
            <div className="space-y-3">
              {upcoming.length === 0 && <div className="text-sm text-[#475569]">No upcoming events.</div>}
              {upcoming.map((e, idx) => {
                const col = TYPE_COLOR[e.type] || TYPE_COLOR.upcoming;
                return (
                  <div key={idx} className="flex items-start gap-3 pb-3 border-b border-[#E2E8F0] last:border-0">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${col.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{e.title}</div>
                      <div className="text-xs text-[#475569] mt-0.5">{formatDate(e.date)}</div>
                    </div>
                    <Badge className="bg-[#F1F5F9] border-0 text-[#475569] capitalize">{e.type}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
